import { Box, LinearProgress } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useI2pDataGridEdit, useI2pSimpleDataGrid } from '@price-for-profit/data-grid';
import { useMemo } from 'react';
import { ONE_HOUR, QUERY_KEYS } from 'shared/constants';
import { useUpdateImportance, useUpdateSegment } from 'shared/mutations';
import { useService } from 'shared/providers';
import { CustomerCommodity, UserInfo } from 'shared/types';
import { determineIsAdmin } from 'shared/utility';
import { columnsCustomerParameters } from './table-def';
import { TableToolbar } from './table-toolbar';

interface TableManageCustomerParametersProps {
  user: UserInfo;
}

export function TableManageCustomerParameters({ user }: TableManageCustomerParametersProps) {
  const isAdmin = determineIsAdmin(user);
  const { customerCommodityService } = useService();

  const columns = useMemo(() => {
    return columnsCustomerParameters({ admin: isAdmin });
  }, [isAdmin]);

  const { getDataGridProps, updatedAt, invalidate } = useI2pSimpleDataGrid<CustomerCommodity>({
    columns,
    name: QUERY_KEYS.customerCommodities,
    getData: async () => {
      return customerCommodityService.getCommodities();
    },
    queryOptions: {
      staleTime: ONE_HOUR,
    },
  });

  const segmentMutation = useUpdateSegment();
  const importanceMutation = useUpdateImportance();

  const { getDataGridEditProps } = useI2pDataGridEdit<CustomerCommodity>({
    onEdit: async (newRow, oldRow) => {
      const newRevisedSegment = newRow.revisedSegment;
      const newRevisedImportance = newRow.revisedImportance;

      if (newRevisedSegment !== oldRow.revisedSegment) {
        const updatedRecords = await segmentMutation.mutateAsync({
          segment: newRevisedSegment,
          masterCustomerCode: newRow.masterCustomerCode,
          userId: user.id,
          userEmail: user.email,
        });

        const updatedRow = updatedRecords.filter(record => record.id === newRow.id)[0];
        return { ...updatedRow };
      }

      if (newRevisedImportance !== oldRow.revisedImportance) {
        const updatedRecords = await importanceMutation.mutateAsync({
          importance: newRevisedImportance,
          commodityId: newRow.commodityId,
          masterCustomerCode: newRow.masterCustomerCode,
          userId: user.id,
          userEmail: user.email,
        });
        const updatedRow = updatedRecords.filter(record => record.id === newRow.id)[0];
        return { ...updatedRow };
      }

      return oldRow;
    },
  });

  const styles = useMemo(
    () =>
      isAdmin
        ? {
            '& .drive-app-wps--cell--editable': {
              bgcolor: '#dbe7f2',
            },
          }
        : undefined,
    [isAdmin]
  );

  return (
    <Box height='calc(100vh - 175px)'>
      <DataGridPro
        {...getDataGridProps()}
        {...getDataGridEditProps()}
        // custom density toolbar
        density='compact'
        localeText={{
          toolbarDensity: 'Row Spacing',
        }}
        components={{
          Toolbar: TableToolbar,
          LoadingOverlay: LinearProgress,
        }}
        componentsProps={{
          toolbar: { invalidate, updatedAt },
        }}
        disableSelectionOnClick
        treeData
        groupingColDef={{
          headerName: 'Commodity Group',
        }}
        getTreeDataPath={row => row.hierarchy}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            textOverflow: 'clip',
            whiteSpace: 'break-spaces',
            lineHeight: 1,
          },
          ...styles,
        }}
      />
    </Box>
  );
}
